/**
 * Lightweight toast notification system.
 *
 * Usage:
 *   import { toast } from '@/components/ui/Toast';
 *   toast.success('Report saved');
 *   toast.error('Failed to delete report');
 *
 * Mount <Toaster /> once in App.tsx to render the toast container.
 */
import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, Info, X } from 'lucide-react';
import { cn } from '@/lib/cn';

// ─── Types ──────────────────────────────────────────────────────────────────

type ToastVariant = 'success' | 'error' | 'info';

/**
 * An optional single action, for the case where the toast reports a refusal the
 * user can lift themselves — being told what blocked you is only half of it.
 */
interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastItem {
  id: string;
  message: string;
  variant: ToastVariant;
  duration: number;
  action?: ToastAction;
}

type AddToast = (message: string, variant?: ToastVariant, duration?: number) => void;

// ─── Global store (works outside React) ─────────────────────────────────────

let listeners: Array<(items: ToastItem[]) => void> = [];
let toasts: ToastItem[] = [];
let idCounter = 0;

function emit() {
  listeners.forEach((fn) => fn([...toasts]));
}

function addToast(
  message: string,
  variant: ToastVariant = 'info',
  duration = 4000,
  action?: ToastAction,
) {
  const id = `toast_${++idCounter}`;
  toasts = [...toasts, { id, message, variant, duration, action }];
  emit();
}

function removeToast(id: string) {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

/** Imperative toast API — call from anywhere. */
export const toast = {
  success: (msg: string, duration?: number) => addToast(msg, 'success', duration),
  error: (msg: string, duration?: number) => addToast(msg, 'error', duration ?? 5000),
  info: (msg: string, duration?: number) => addToast(msg, 'info', duration),
  /**
   * A refusal the user can act on. Given a longer life than a plain error,
   * because it is worthless if it disappears before the button is pressed.
   */
  action: (msg: string, action: ToastAction, duration?: number) =>
    addToast(msg, 'error', duration ?? 12_000, action),
};

// ─── Variant styling ────────────────────────────────────────────────────────

const ICONS: Record<ToastVariant, React.ReactNode> = {
  success: <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />,
  error: <XCircle className="w-4 h-4 text-red-500 shrink-0" />,
  info: <Info className="w-4 h-4 text-brand-500 shrink-0" />,
};

const BG: Record<ToastVariant, string> = {
  success: 'bg-white border-emerald-200',
  error: 'bg-white border-red-200',
  info: 'bg-white border-slate-200',
};

// ─── Single Toast ───────────────────────────────────────────────────────────

function ToastCard({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onDismiss, item.duration);
    return () => clearTimeout(timer);
  }, [item.duration, onDismiss]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -8, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.96 }}
      transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
      className={cn(
        'flex items-start gap-2.5 px-4 py-3 rounded-xl border shadow-lg shadow-black/5',
        'max-w-sm w-full pointer-events-auto',
        BG[item.variant],
      )}
    >
      {ICONS[item.variant]}
      <div className="flex-1 min-w-0">
        <span className="text-sm text-slate-700 leading-snug">{item.message}</span>
        {item.action && (
          <button
            onClick={() => {
              item.action?.onClick();
              onDismiss();
            }}
            className="mt-2 block text-xs font-medium text-brand-600 hover:text-brand-700 hover:underline"
          >
            {item.action.label}
          </button>
        )}
      </div>
      <button
        onClick={onDismiss}
        className="p-0.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors shrink-0"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </motion.div>
  );
}

// ─── Toaster (mount once in App) ────────────────────────────────────────────

export function Toaster() {
  const [items, setItems] = useState<ToastItem[]>([]);

  useEffect(() => {
    listeners.push(setItems);
    return () => {
      listeners = listeners.filter((fn) => fn !== setItems);
    };
  }, []);

  return createPortal(
    <div className="fixed top-4 right-4 z-[9999] flex flex-col items-end gap-2 pointer-events-none">
      <AnimatePresence mode="popLayout">
        {items.map((item) => (
          <ToastCard
            key={item.id}
            item={item}
            onDismiss={() => removeToast(item.id)}
          />
        ))}
      </AnimatePresence>
    </div>,
    document.body,
  );
}
